import { ListItemCoreSize, ListItemCoreWeight } from './ListItemCore';
import { Theme, css } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';

// following example in buttonCssDefs.ts - will move this const when we agree on its placement
export const shadowSize = '2px';

export const listItemRoot = css`
  list-style: none;
  position: relative;
  width: 100%;
`;

/**
 * Component prop classes
 */

const calculateMinHeight = (
  { spacing }: Theme,
  {
    listItemSize,
    interactiveElementSize,
  }: { listItemSize: ListItemCoreSize; interactiveElementSize?: number }
) => {
  let minHeight;
  const minHeightWithInteractiveElement = interactiveElementSize
    ? interactiveElementSize + spacing(1.5)
    : 0;
  switch (listItemSize) {
    case 'medium':
      minHeight = `${Math.max(44, minHeightWithInteractiveElement)}px`;
      break;
    case 'large':
      minHeight = `${Math.max(52, minHeightWithInteractiveElement)}px`;
      break;
    default:
      assertNever(listItemSize);
  }

  return {
    minHeight,
  };
};

/**
 * Component traits (boolean states)
 */

export const listItemSlimLeft = ({ breakpoints, spacing }: Theme) => css`
  padding-left: ${spacing(1.5)}px;

  ${breakpoints.up('sm')} {
    padding-left: ${spacing(2.5)}px;
  }
`;

export const listItemSlimRight = ({ breakpoints, spacing }: Theme) => css`
  padding-right: ${spacing(1.5)}px;

  ${breakpoints.up('sm')} {
    padding-right: ${spacing(2.5)}px;
  }
`;

export const listItemTall = ({ breakpoints, spacing }: Theme) => css`
  padding-bottom: ${spacing(2)}px;
  padding-top: ${spacing(2)}px;

  ${breakpoints.up('sm')} {
    padding-bottom: ${spacing(2)}px;
    padding-top: ${spacing(2)}px;
  }
`;

// Account for necessary padding when absolutely positioning an interactive end element
const getBaseInlinePadding = (
  { breakpoints, spacing }: Theme,
  interactiveElementSize: number | undefined
) => {
  const interactivePadding =
    interactiveElementSize === undefined ? 0 : interactiveElementSize + spacing(2);
  const paddingRight = spacing(2.5) + interactivePadding;
  const upFromSmPaddingRight = spacing(3.5) + interactivePadding;
  return css`
    padding-left: ${spacing(2.5)}px;
    padding-right: ${paddingRight}px;
    ${breakpoints.up('sm')} {
      padding-left: ${spacing(3.5)}px;
      padding-right: ${upFromSmPaddingRight}px;
    }
  `;
};

/**
 * The base component
 */
export const listItemBase = (
  theme: Theme,
  {
    interactiveElementSize,
    selected,
    listItemSize,
  }: {
    selected?: boolean;
    interactiveElementSize: number | undefined;
    listItemSize: ListItemCoreSize;
    listItemWeight: ListItemCoreWeight;
  }
) => {
  const { minHeight } = calculateMinHeight(theme, {
    listItemSize,
    interactiveElementSize,
  });
  return [
    css`
      --color-icon: ${selected ? theme.colors.fgPrimaryBase : theme.colors.fgDefault};
      align-items: center;
      background-color: ${theme.colors.bgDefault};
      box-shadow: ${theme.boxShadows.dropShadowNone};
      box-sizing: border-box;
      color: ${selected ? theme.colors.fgPrimaryBase : theme.colors.fgDefault};
      display: flex;
      min-height: ${minHeight};
      outline: none;
      padding-top: ${theme.spacing(1.5)}px;
      padding-bottom: ${theme.spacing(1.5)}px;
      ${getBaseInlinePadding(theme, interactiveElementSize)};
      position: relative;
      text-align: left;
      transition: all ${theme.timings.duration.shortest} ${theme.timings.timingFunction.easeInOut};
      width: 100%;
      z-index: initial;

      /* Spacing between elementStart and text content */
      & > :not(:first-of-type) {
        margin-left: ${theme.spacing(2)}px;
      }

      /* Element-specific styles */
      a& {
        text-decoration: none;
      }

      button& {
        appearance: none;
      }

      button&:not(:disabled):not([aria-disabled]) {
        cursor: pointer;
      }

      ${theme.interactiveStates.active(css`
        background-color: ${theme.colors.bgLight};
      `)};

      ${theme.interactiveStates.focus(css`
        box-shadow: inset 0 0 0 ${shadowSize} ${theme.colors.borderPrimaryBase};
        z-index: ${theme.zIndex.bump};
      `)};

      ${theme.interactiveStates.hover(css`
        ${listItemHoverStyles(theme)}
      `)};
    `,
    !selected &&
      theme.interactiveStates.disabled(css`
        --color-icon: ${theme.colors.fgDisabled};
        --color-secondary: ${theme.colors.fgDisabled};
        background-color: ${theme.colors.bgDefault};
        color: ${theme.colors.fgDisabled};
      `),
    listItemLeftDecoration(theme, selected),
  ];
};

export const listItemHoverStyles = (theme: Theme) => css`
  --line-highlight-color: ${theme.colors.borderPrimaryBase};
  background-color: ${theme.colors.bgLighter};
`;

export const listItemLeftDecoration = (theme: Theme, selected?: boolean) => {
  const listItemHoverLeftHandBorder = '4px';
  return css`
    &::before {
      background-color: var(
        --line-highlight-color,
        ${selected ? theme.colors.bgPrimaryBase : 'transparent'}
      );
      calc(${listItemHoverLeftHandBorder} * 0.5) 0;
      bottom: 0;
      content: '';
      left: 0;
      position: absolute;
      top: 0;
      transition: all ${theme.timings.duration.shortest} ${theme.timings.timingFunction.easeInOut};
      width: ${listItemHoverLeftHandBorder};
    }
  `;
};

/**
 * Component children
 */

export const listItemContent = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const listItemText = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

export const listItemPrimaryText = (
  { typography }: Theme,
  listItemSize: ListItemCoreSize,
  listItemWeight: ListItemCoreWeight
) => {
  switch (listItemWeight) {
    case 'book':
      switch (listItemSize) {
        case 'medium':
          return css`
            ${typography.variant('body02')},
            ${listItemText};
          `;
        case 'large':
          return css`
            ${typography.variant('body01')},
            ${listItemText};
          `;
      }
      break;
    case 'medium':
      switch (listItemSize) {
        case 'medium':
          return css`
            ${typography.variant('body02Strong')},
            ${listItemText};
          `;
        case 'large':
          return css`
            ${typography.variant('body01Strong')},
            ${listItemText};
          `;
      }
      break;
    default:
      assertNever(listItemWeight);
  }
};

export const listItemSecondaryText = (theme: Theme) => {
  return css`
    color: var(--color-secondary, ${theme.colors.fgSubtle});
    ${theme.typography.variant('caption')};
    ${listItemText};
    margin-top: ${theme.spacing(0.25)}px;
  `;
};

export const listItemElement = ({ colors }: Theme) => css`
  color: var(--color-icon, ${colors.fgDefault});
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const listItemInteractiveElementEnd = ({
  breakpoints,
  colors,
  spacing,
  zIndex,
}: Theme) => css`
  color: var(--color-icon, ${colors.fgDefault});
  position: absolute;
  right: ${spacing(2.5)}px;
  top: 50%;
  transform: translateY(-50%);
  z-index: ${zIndex.bump};

  ${breakpoints.up('sm')} {
    right: ${spacing(3.5)}px;
  }
`;
